import { ScopeId } from './scope'
export const features = [
  'analytics',
  'adx',
  'viewability',
  'openBidding',
  'refresh',
  'userSession',
  'standaloneUserSession',
  'gamOverviewWeb',
  'gamOverviewApp',
  'gamOverviewAmp',
  'adstack',
  'abtest',
  'smartAdServer',
  'admRefresh',
  'marketplace',
  'marketplaceTeaser',
  'instream',
  'interstitial',
  'coreWebVitals',
  'pbsStandalone',
  'pbstckBidder',
  'sparkForNetwork',
  'sparkForPub',
  'inapp',
  'amp',
] as const

export type Feature = (typeof features)[number]

// custom declaration to enable includes(string) on features readonly array
// see https://stackoverflow.com/questions/56565528/typescript-const-assertions-how-to-use-array-prototype-includes
declare global {
  interface ReadonlyArray<T> {
    includes<U>(x: U & (T & U extends never ? never : unknown)): boolean
  }
}

export type AccessRole = 'user' | 'owner' | 'pubstack'
// Demo is not an access role, it's a normal user with some rename stuff
export type Role = AccessRole | 'demo'
export type UserViewMode = 'owner' | 'user' | 'demo'
export interface PubstackUserRole {
  pubstack: true | 'tech-product'
  view?: UserViewMode
}
export interface NonPubstackUserRole {
  [scopeId: string]: Role // {'d5629df8-a263-4238-a3ab-efc2ececbe81': 'user'}
}
export type UserRole = PubstackUserRole | NonPubstackUserRole

export const isPubstackUserRole = (userRole: UserRole): userRole is PubstackUserRole => userRole.pubstack === true || userRole.pubstack === 'tech-product'
export const isNonPubstackUserRole = (userRole: UserRole): userRole is NonPubstackUserRole => !isPubstackUserRole(userRole)

export const getScopeRole = (scopeId: ScopeId, userRole: UserRole): Role | null => {
  if (isPubstackUserRole(userRole)) {
    return 'pubstack'
  }
  return userRole[scopeId] || null
}

export const scopesAllowed = (userRole: UserRole): ScopeId[] | 'all' => {
  if (isPubstackUserRole(userRole)) return 'all'
  return Object.keys(userRole)
}

export const canAccess = (scopeId: ScopeId, userRole: UserRole, accessRole: AccessRole): boolean => {
  switch (getScopeRole(scopeId, userRole)) {
    case null:
      return false
    case 'pubstack':
      return true
    case 'owner':
      return accessRole === 'owner' || accessRole === 'user'
    default:
      return accessRole === 'user'
  }
}

export const canAccessScopeId = (scopeId: ScopeId, userRole: UserRole): boolean => {
  if (isPubstackUserRole(userRole)) {
    return true
  } else {
    return scopeId in userRole
  }
}
export const scopeFeature = (scopeId: ScopeId, feature: Feature): string => `${scopeId}:feature:${feature}`

export const hasFeature = (scopeId: ScopeId, features: string[], feature: Feature): boolean => {
  return features.findIndex((scopedFeature) => scopedFeature === scopeFeature(scopeId, feature)) !== -1
}

export const scopeAllFeatures = (scopeId: ScopeId): string[] => features.map((feature) => scopeFeature(scopeId, feature))
